/**
 * Automations & Webhooks Management Page
 * Allows users to create, view, edit, and delete automation webhooks for Zapier and other integrations
 */

import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  AutomationWebhook,
  AutomationWebhookCreate,
  AutomationWebhookEvent,
} from '@/types/automation';
import AutomationWebhookConfig from '@/components/AutomationWebhookConfig';
import AutomationSetupWizard from '@/components/AutomationSetupWizard';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Plus, Trash2, Edit2, Eye, EyeOff, Play, BarChart3, CheckCircle, XCircle, Clock, Zap, Copy, FileText, Info, Square, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function Automations() {
  const { tenant } = useAuth();
  const [webhooks, setWebhooks] = useState<AutomationWebhook[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<AutomationWebhook | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [featureAvailable, setFeatureAvailable] = useState<boolean | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [events, setEvents] = useState<AutomationWebhookEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [metrics, setMetrics] = useState<any>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [testData, setTestData] = useState('{\n  "email": "test@example.com",\n  "first_name": "Test",\n  "last_name": "User"\n}');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [webhookTemplates, setWebhookTemplates] = useState<any[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [webhookToClone, setWebhookToClone] = useState<AutomationWebhook | null>(null);
  const [presetTestData, setPresetTestData] = useState<Record<string, string>>({});
  const [showWizard, setShowWizard] = useState(false);
  const [selectedTemplateForWizard, setSelectedTemplateForWizard] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [selectedWebhookIds, setSelectedWebhookIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [wizardKey, setWizardKey] = useState(0); // Unique key to force wizard remount
  const [webhookMetricsMap, setWebhookMetricsMap] = useState<Record<string, any>>({}); // Store metrics for each webhook

  const tenantId = tenant?.id;

  // Debug: Log when wizard state changes
  useEffect(() => {
    console.log('🟢 Automations page: showWizard changed to:', showWizard);
    console.log('🟢 Automations page: wizardKey is:', wizardKey);
    if (showWizard) {
      console.log('🟢 Automations page: Wizard is OPENING with key:', wizardKey);
    } else {
      console.log('🟢 Automations page: Wizard is CLOSED');
    }
  }, [showWizard, wizardKey]);

  useEffect(() => {
    if (tenant) {
      checkFeatureAvailability();
    }
  }, [tenant]);

  useEffect(() => {
    if (featureAvailable === true && tenantId) {
      loadWebhooks();
    }
  }, [featureAvailable, tenantId]);

  useEffect(() => {
    if (featureAvailable === true) {
      loadPresetTestData();
    }
  }, [featureAvailable]);

  const checkFeatureAvailability = async () => {
    try {
      const response = await api.billing.getFeatures();
      const data = response.data;
      // Check for zapier_integration or api_access feature (both indicate Growth+)
      setFeatureAvailable(data.features?.zapier_integration || data.features?.api_access || false);
      setCurrentPlan(data.plan || 'starter');
    } catch (error) {
      console.error('Failed to check feature availability:', error);
      setFeatureAvailable(false);
    }
  };

  const loadWebhooks = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      const response = await api.automation.getWebhooks(tenantId);
      const data = response.data;
      const webhooksList = Array.isArray(data) ? data : [];
      setWebhooks(webhooksList);
      
      // Load metrics for all webhooks
      const metricsPromises = webhooksList.map(async (webhook: AutomationWebhook) => {
        try {
          const metricsResponse = await api.automation.getWebhookMetrics(webhook.id);
          return { webhookId: webhook.id, metrics: metricsResponse.data };
        } catch (error) {
          console.error(`Failed to load metrics for webhook ${webhook.id}:`, error);
          return { webhookId: webhook.id, metrics: null };
        }
      });
      
      const metricsResults = await Promise.all(metricsPromises);
      const metricsMap: Record<string, any> = {};
      metricsResults.forEach(({ webhookId, metrics }) => {
        if (metrics) {
          metricsMap[webhookId] = metrics;
        }
      });
      setWebhookMetricsMap(metricsMap);
    } catch (error: any) {
      console.error('Failed to load webhooks:', error);
      if (error.response?.status === 403) {
        setFeatureAvailable(false);
      } else {
        toast.error('Failed to load automation webhooks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (webhook: AutomationWebhook) => {
    try {
      setLoadingEvents(true);
      const response = await api.automation.getWebhookEvents(webhook.id, 50);
      const data = response.data;
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load webhook events:', error);
      toast.error('Failed to load webhook events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadMetrics = async (webhook: AutomationWebhook) => {
    try {
      setLoadingMetrics(true);
      const response = await api.automation.getWebhookMetrics(webhook.id);
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to load webhook metrics:', error);
      toast.error('Failed to load webhook metrics');
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleTestWebhook = async (webhook: AutomationWebhook) => {
    try {
      const parsedData = JSON.parse(testData);
      setTesting(true);
      const response = await api.automation.testWebhook(webhook.id, parsedData);
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success('Webhook test successful!');
      } else {
        toast.error('Webhook test failed');
      }
    } catch (error: any) {
      console.error('Failed to test webhook:', error);
      const errorMessage = error?.response?.data?.detail || 'Invalid JSON or test failed';
      toast.error(errorMessage);
      setTestResult({
        success: false,
        errors: [errorMessage]
      });
    } finally {
      setTesting(false);
    }
  };

  const handleCreateClick = () => {
    console.log('🟢 Automations: handleCreateClick called');
    setSelectedWebhook(null);
    setWizardKey(prev => {
      const newKey = prev + 1;
      console.log('🟢 Automations: Incrementing wizardKey from', prev, 'to', newKey);
      return newKey;
    });
    setShowWizard(true);
    setShowForm(false);
    setSelectedTemplateForWizard(null);
    setEvents([]);
    setShowEvents(false);
    
    // Scroll to wizard after a brief delay to ensure it renders
    setTimeout(() => {
      const wizardElement = document.querySelector('[data-wizard="automation-setup"]');
      if (wizardElement) {
        wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleEditWebhook = (webhook: AutomationWebhook) => {
    setSelectedWebhook(webhook);
    setShowEditModal(true);
    setShowEvents(false);
    setEvents([]);
  };

  const handleViewWebhook = (webhook: AutomationWebhook) => {
    setSelectedWebhook(webhook);
    setShowForm(false);
    setShowEvents(true);
    setShowMetrics(false);
    loadEvents(webhook);
  };

  const handleViewMetrics = (webhook: AutomationWebhook) => {
    setSelectedWebhook(webhook);
    setShowMetricsModal(true);
    loadMetrics(webhook);
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.automation.getWebhookTemplates();
      setWebhookTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to load webhook templates:', error);
      toast.error('Failed to load webhook templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleClone = async () => {
    if (!webhookToClone || !cloneName.trim()) return;
    
    try {
      setCloning(true);
      await api.automation.cloneWebhook(webhookToClone.id, cloneName);
      toast.success('Webhook cloned successfully');
      setShowCloneModal(false);
      setCloneName('');
      setWebhookToClone(null);
      loadWebhooks(); // Refresh list
    } catch (error: any) {
      console.error('Clone failed:', error);
      toast.error('Clone failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCloning(false);
    }
  };

  const startClone = (webhook: AutomationWebhook) => {
    setWebhookToClone(webhook);
    setCloneName(webhook.name + ' (Copy)');
    setShowCloneModal(true);
  };

  const loadPresetTestData = () => {
    const presets: Record<string, string> = {
      // General
      'general': JSON.stringify({
        "email": "test@example.com",
        "first_name": "John",
        "last_name": "Smith", 
        "phone": "+1555123456",
        "company": "Test Company"
      }, null, 2),
      
      // CRM
      'hubspot': JSON.stringify({
        "vid": "12345",
        "properties": {
          "email": {"value": "john.doe@example.com"},
          "firstname": {"value": "John"},
          "lastname": {"value": "Doe"},
          "phone": {"value": "+1234567890"},
          "company": {"value": "Example Corp"},
          "hs_lead_status": {"value": "new"}
        }
      }, null, 2),
      
      'salesforce': JSON.stringify({
        "Id": "0064W00000ABC123", 
        "Name": "Example Deal",
        "StageName": "Closed Won",
        "Amount": 50000,
        "CloseDate": "2024-01-15",
        "Account": {
          "Name": "Example Corp",
          "Email__c": "contact@example.com"
        }
      }, null, 2),
      
      // Forms
      'typeform': JSON.stringify({
        "form_response": {
          "answers": {
            "email": "prospect@example.com",
            "first_name": "Sarah",
            "last_name": "Johnson",
            "phone": "+1555123456",
            "company": "Tech Startup",
            "interest_level": "High"
          }
        }
      }, null, 2),
      
      // Veterinary
      'veterinary': JSON.stringify({
        "client_email": "petowner@example.com",
        "client_first_name": "Jennifer",
        "client_last_name": "Brown",
        "client_phone": "+1555123456",
        "pet_name": "Buddy",
        "pet_species": "Dog",
        "pet_breed": "Golden Retriever",
        "procedure_name": "Dental Surgery",
        "appointment_date": "2024-01-15",
        "doctor_name": "Dr. Smith"
      }, null, 2),
      
      // Medical
      'medical': JSON.stringify({
        "patient_email": "patient@example.com",
        "patient_first_name": "Robert",
        "patient_last_name": "Johnson", 
        "patient_phone": "+1555123456",
        "appointment_date": "2024-01-20",
        "appointment_time": "10:00 AM",
        "provider": "Dr. Wilson",
        "appointment_type": "Annual Physical"
      }, null, 2),
      
      // Dental
      'dental': JSON.stringify({
        "patient_email": "patient@example.com",
        "patient_first_name": "Michael",
        "patient_last_name": "Davis",
        "patient_phone": "+1555456789",
        "procedure": "Cleaning",
        "appointment_date": "2024-01-15",
        "dentist": "Dr. Smith"
      }, null, 2)
    };

    setPresetTestData(presets);
  };

  const handleTestWebhookClick = (webhook: AutomationWebhook) => {
    setSelectedWebhook(webhook);
    setShowTestModal(true);
    setTestResult(null);
    
    // Load preset test data
    loadPresetTestData();
    
    // Set default test data
    const sampleData = {
      email: "test@example.com",
      first_name: "Test",
      last_name: "User",
      phone: "+1234567890",
      company: "Test Company"
    };
    setTestData(JSON.stringify(sampleData, null, 2));
  };

  const handleSave = async (data: AutomationWebhookCreate) => {
    if (!tenantId) {
      toast.error('No tenant selected');
      return null;
    }

    try {
      if (selectedWebhook) {
        // Update existing webhook
        const { tenant_id, ...updateData } = data as any;
        const response = await api.automation.updateWebhook(selectedWebhook.id, updateData);
        toast.success('Webhook updated successfully');
        setShowForm(false);
        setSelectedWebhook(null);
        await loadWebhooks();
        return response.data; // Return updated webhook
      } else {
        // Create new webhook
        const response = await api.automation.createWebhook({
          ...data,
          tenant_id: tenantId,
        });
        toast.success('Webhook created successfully');
        setShowForm(false);
        setSelectedWebhook(null);
        await loadWebhooks();
        return response.data; // Return created webhook
      }
    } catch (error: any) {
      console.error('Failed to save webhook:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save webhook');
      return null;
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setShowWizard(false);
    setSelectedWebhook(null);
    setSelectedTemplateForWizard(null);
    setEvents([]);
    setShowEvents(false);
    setShowMetrics(false);
    setShowTestModal(false);
    setShowEditModal(false);
    setShowMetricsModal(false);
    setMetrics(null);
    setTestResult(null);
  };

  const handleToggleActive = async (webhook: AutomationWebhook) => {
    try {
      const updateData = { is_active: !webhook.is_active };
      await api.automation.updateWebhook(webhook.id, updateData);
      toast.success(`Automation ${updateData.is_active ? 'activated' : 'deactivated'} successfully`);
      if (tenantId) {
        await loadWebhooks();
      }
    } catch (error: any) {
      console.error('Failed to toggle webhook status:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update automation status');
    }
  };

  const handleDelete = async (webhook: AutomationWebhook) => {
    if (!confirm(`Are you sure you want to delete the webhook "${webhook.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.automation.deleteWebhook(webhook.id);
      toast.success('Webhook deleted successfully');
      if (tenantId) {
        await loadWebhooks();
      }
      if (selectedWebhook?.id === webhook.id) {
        setSelectedWebhook(null);
        setShowForm(false);
        setEvents([]);
        setShowEvents(false);
        setShowMetrics(false);
        setShowTestModal(false);
        setMetrics(null);
      }
      // Remove from selection if it was selected
      setSelectedWebhookIds(prev => prev.filter(id => id !== webhook.id));
    } catch (error: any) {
      console.error('Failed to delete webhook:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete webhook');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedWebhookIds.length === 0) return;

    const selectedWebhooks = webhooks.filter(w => selectedWebhookIds.includes(w.id));
    const webhookNames = selectedWebhooks.map(w => w.name).join(', ');
    
    if (!confirm(`Are you sure you want to delete ${selectedWebhookIds.length} webhook(s)?\n\n${webhookNames}\n\nThis action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    const errors: string[] = [];
    const successes: string[] = [];

    try {
      // Delete each webhook
      for (const webhookId of selectedWebhookIds) {
        try {
          await api.automation.deleteWebhook(webhookId);
          const webhook = webhooks.find(w => w.id === webhookId);
          if (webhook) {
            successes.push(webhook.name);
          }
        } catch (error: any) {
          const webhook = webhooks.find(w => w.id === webhookId);
          const name = webhook?.name || webhookId;
          errors.push(`${name}: ${error?.response?.data?.detail || 'Failed to delete'}`);
        }
      }

      // Clear selection
      setSelectedWebhookIds([]);

      // Show results
      if (successes.length > 0) {
        toast.success(`Successfully deleted ${successes.length} webhook(s)`);
      }
      if (errors.length > 0) {
        toast.error(`Failed to delete ${errors.length} webhook(s). Check console for details.`);
        console.error('Bulk delete errors:', errors);
      }

      // Reload webhooks
      if (tenantId) {
        await loadWebhooks();
      }

      // Clear selected webhook if it was deleted
      if (selectedWebhook && selectedWebhookIds.includes(selectedWebhook.id)) {
        setSelectedWebhook(null);
        setShowForm(false);
        setEvents([]);
        setShowEvents(false);
        setShowMetrics(false);
        setShowTestModal(false);
        setMetrics(null);
      }
    } catch (error: any) {
      console.error('Failed to delete webhooks:', error);
      toast.error('An error occurred during bulk delete');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectWebhook = (webhookId: string) => {
    setSelectedWebhookIds(prev => {
      if (prev.includes(webhookId)) {
        return prev.filter(id => id !== webhookId);
      } else {
        return [...prev, webhookId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedWebhookIds.length === webhooks.length) {
      // Deselect all
      setSelectedWebhookIds([]);
    } else {
      // Select all
      setSelectedWebhookIds(webhooks.map(w => w.id));
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (webhook: AutomationWebhook) => {
    if (!webhook.is_active) {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Inactive
        </span>
      );
    }
    return (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
        Active
      </span>
    );
  };

  const getAutomationStatus = (webhook: AutomationWebhook) => {
    if (!webhook.is_active) {
      return {
        status: 'inactive',
        message: 'Automation paused',
        description: 'Click "Activate" to start processing customers automatically',
        color: 'gray',
        icon: Clock
      };
    }
    
    if (webhook.trigger_count === 0) {
      return {
        status: 'waiting',
        message: 'Active and ready',
        description: 'Waiting for first webhook trigger - make sure your integration is configured correctly',
        color: 'blue',
        icon: Zap
      };
    }
    
    if (webhook.last_triggered_at) {
      const lastTriggered = new Date(webhook.last_triggered_at);
      const daysSince = Math.floor((Date.now() - lastTriggered.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysSince > 7) {
        return {
          status: 'stale',
          message: `Last worked ${daysSince} days ago`,
          description: 'May need attention - check your integration',
          color: 'yellow',
          icon: AlertTriangle
        };
      }
    }
    
    // Use metrics if available, otherwise fall back to trigger_count
    const webhookMetrics = webhookMetricsMap[webhook.id];
    const successfulCount = webhookMetrics?.successful_triggers ?? 0;
    const displayCount = successfulCount > 0 ? successfulCount : (webhook.trigger_count || 0);
    
    return {
      status: 'working',
      message: `Working perfectly! ${displayCount.toLocaleString()} ${successfulCount > 0 ? 'customers processed' : 'requests received'}`,
      description: webhook.last_triggered_at ? `Last: ${formatDate(webhook.last_triggered_at)}` : '',
      color: 'green',
      icon: CheckCircle
    };
  };

  const getBusinessTypeDisplay = (webhook: AutomationWebhook) => {
    const metadata = webhook.metadata || {};
    const businessType = metadata.business_type || 'general';
    const automationType = metadata.automation_type || 'automation';
    
    const typeMap = {
      'dental': '🦷 Dental Practice',
      'veterinary': '🐾 Veterinary Practice',
      'medical': '🏥 Medical Practice',
      'ecommerce': '🛒 E-commerce',
      'service': '🔧 Service Business',
      'general': '💼 Business'
    };
    
    return typeMap[businessType] || '💼 Business';
  };

  // Show upgrade prompt if feature is not available
  if (featureAvailable === false) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Automations Not Available</h2>
          <p className="text-gray-600 mb-6">
            Automation webhooks are only available on the <strong>Growth</strong> plan or higher.
            Your current plan (<strong className="capitalize">{currentPlan}</strong>) does not include this feature.
          </p>
          <div className="flex gap-4 justify-center">
            <a
              href="/billing"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Upgrade to Growth Plan
            </a>
            <button
              onClick={() => window.history.back()}
              className="inline-block px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-lg text-gray-500">Loading automation webhooks...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">🚀 Automate Your Customer Follow-up</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Save hours every week by automatically sending emails when customers take actions. 
            Perfect for welcoming new clients, following up after appointments, and staying in touch.
          </p>
          
          {/* Show progress restoration message */}
          {localStorage.getItem(`automation-wizard-progress-${tenantId}`) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-md mx-auto mb-4">
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <Info className="h-4 w-4" />
                <span>You have a saved automation in progress. Continue where you left off!</span>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => {
                setWizardKey(prev => prev + 1); // Increment key to force remount
                setShowWizard(true);
                setTimeout(() => {
                  const wizardElement = document.querySelector('[data-wizard="automation-setup"]');
                  if (wizardElement) {
                    wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
            >
              <Zap className="h-5 w-5 mr-2" />
              Set Up Your First Automation
            </Button>
            
            <Button 
              onClick={() => {
                setShowTemplates(true);
                loadTemplates();
              }} 
              variant="outline" 
              size="lg"
              className="px-8 py-3 text-lg"
            >
              <FileText className="h-5 w-5 mr-2" />
              Browse Templates
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-8 mt-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>5-minute setup</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>No coding required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Works with 5000+ apps</span>
            </div>
          </div>
        </div>
      </div>

      {/* Automations List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Your Automations</h2>
          <div className="flex items-center gap-3">
            {webhooks.length > 0 && selectedWebhookIds.length > 0 && (
              <Button 
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4" />
                {isDeleting ? 'Deleting...' : `Delete ${selectedWebhookIds.length} Selected`}
              </Button>
            )}
            {webhooks.length > 0 && (
              <Button 
                onClick={() => {
                  setWizardKey(prev => prev + 1); // Increment key to force remount
                  setShowWizard(true);
                  setTimeout(() => {
                    const wizardElement = document.querySelector('[data-wizard="automation-setup"]');
                    if (wizardElement) {
                      wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Another Automation
              </Button>
            )}
          </div>
        </div>

        {/* Bulk Selection Toolbar */}
        {webhooks.length > 0 && (
          <div className="flex items-center justify-between gap-4 mb-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200 shadow-sm">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                type="button"
              >
                {selectedWebhookIds.length === webhooks.length ? (
                  <CheckSquare className="h-5 w-5 text-blue-600" />
                ) : (
                  <Square className="h-5 w-5 text-gray-500 hover:text-blue-600 transition-colors" />
                )}
                <span className="font-semibold">
                  {selectedWebhookIds.length === webhooks.length ? 'Deselect All' : 'Select All'}
                </span>
              </button>
              {selectedWebhookIds.length > 0 && (
                <span className="text-sm font-medium text-gray-700 bg-white px-3 py-1 rounded-md border border-gray-300">
                  {selectedWebhookIds.length} of {webhooks.length} selected
                </span>
              )}
            </div>
            {selectedWebhookIds.length > 0 && (
              <Button 
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white shadow-sm"
                size="sm"
              >
                <Trash2 className="h-4 w-4" />
                {isDeleting ? 'Deleting...' : `Delete ${selectedWebhookIds.length}`}
              </Button>
            )}
          </div>
        )}

        {webhooks.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Ready to Save Time?</h3>
              <p className="text-gray-600 mb-6">
                Set up your first automation to automatically follow up with customers. 
                Most businesses save 5-10 hours per week!
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => {
                    setWizardKey(prev => prev + 1); // Increment key to force remount
                    setShowWizard(true);
                    setTimeout(() => {
                      const wizardElement = document.querySelector('[data-wizard="automation-setup"]');
                      if (wizardElement) {
                        wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  size="lg"
                  className="w-full"
                >
                  <Zap className="h-5 w-5 mr-2" />
                  Create Your First Automation
                </Button>
                <Button 
                  onClick={() => {
                    setShowTemplates(true);
                    loadTemplates();
                  }} 
                  variant="outline"
                  size="lg"
                  className="w-full"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  View Examples First
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {webhooks.map((webhook) => {
              const status = getAutomationStatus(webhook);
              const StatusIcon = status.icon;
              
              const isSelected = selectedWebhookIds.includes(webhook.id);
              
              return (
                <Card key={webhook.id} className={`hover:shadow-lg transition-shadow ${isSelected ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}>
                  <CardContent className="p-6">
                    {/* Header with Checkbox */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        <button
                          onClick={() => handleSelectWebhook(webhook.id)}
                          className="mt-1 cursor-pointer hover:scale-110 transition-transform"
                          type="button"
                          aria-label={isSelected ? 'Deselect webhook' : 'Select webhook'}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-6 w-6 text-blue-600" />
                          ) : (
                            <Square className="h-6 w-6 text-gray-400 hover:text-blue-600 transition-colors" />
                          )}
                        </button>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900">{webhook.name}</h3>
                          <p className="text-sm text-gray-600">{getBusinessTypeDisplay(webhook)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {webhook.auto_enqueue_campaign_id && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            <Zap className="h-3 w-3 mr-1" />
                            Auto-emails
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div 
                      className={`p-3 rounded-lg mb-4 ${
                        status.color === 'green' ? 'bg-green-50 border border-green-200' :
                        status.color === 'blue' ? 'bg-blue-50 border border-blue-200' :
                        status.color === 'yellow' ? 'bg-yellow-50 border border-yellow-200' :
                        'bg-gray-50 border border-gray-200'
                      } ${
                        status.status === 'inactive' ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-25' : ''
                      }`}
                      onClick={status.status === 'inactive' ? () => handleToggleActive(webhook) : undefined}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`h-4 w-4 ${
                            status.color === 'green' ? 'text-green-600' :
                            status.color === 'blue' ? 'text-blue-600' :
                            status.color === 'yellow' ? 'text-yellow-600' :
                            'text-gray-600'
                          }`} />
                          <span className={`font-medium text-sm ${
                            status.color === 'green' ? 'text-green-800' :
                            status.color === 'blue' ? 'text-blue-800' :
                            status.color === 'yellow' ? 'text-yellow-800' :
                            'text-gray-800'
                          }`}>
                            {status.message}
                          </span>
                        </div>
                        {/* Activation Toggle */}
                        {webhook.is_active ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleActive(webhook);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleActive(webhook);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Activate
                          </Button>
                        )}
                      </div>
                      {status.description && (
                        <p className={`text-xs mt-1 ${
                          status.color === 'green' ? 'text-green-700' :
                          status.color === 'blue' ? 'text-blue-700' :
                          status.color === 'yellow' ? 'text-yellow-700' :
                          'text-gray-700'
                        }`}>
                          {status.description}
                        </p>
                      )}
                    </div>

                    {/* Data Source */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm text-gray-600">Connected via:</span>
                      <span className="capitalize font-medium text-gray-900">{webhook.webhook_type}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestWebhookClick(webhook)}
                        className="flex items-center gap-1"
                      >
                        <Play className="h-3 w-3" />
                        Test
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewMetrics(webhook)}
                        className="flex items-center gap-1"
                      >
                        <BarChart3 className="h-3 w-3" />
                        Stats
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditWebhook(webhook)}
                        className="flex items-center gap-1"
                      >
                        <Edit2 className="h-3 w-3" />
                        Edit
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startClone(webhook)}
                        className="flex items-center gap-1"
                      >
                        <Copy className="h-3 w-3" />
                        Copy
                      </Button>
                    </div>
                    
                    {/* Delete button - separate and smaller */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(webhook)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Wizard Section */}
      {showWizard && tenantId && (
        <div className="bg-white shadow rounded-lg mb-6" data-wizard="automation-setup">
          <AutomationSetupWizard
            key={`wizard-${wizardKey}`} // Unique key to force remount each time wizard opens
            onComplete={async (webhookData) => {
              const savedWebhook = await handleSave(webhookData);
              return savedWebhook; // Return webhook so wizard can use it
            }}
            onCancel={() => {
              setShowWizard(false);
              setWizardKey(prev => prev + 1); // Increment key to ensure fresh start next time
            }}
            tenantId={tenantId}
            selectedTemplate={selectedTemplateForWizard}
            onWebhookCreated={(webhook) => {
              // Close wizard and open edit modal with the new webhook
              setShowWizard(false);
              setWizardKey(prev => prev + 1); // Increment key to ensure fresh start next time
              setSelectedWebhook(webhook);
              setShowEditModal(true);
              toast.success('Automation created! Configure your Zapier integration below.');
            }}
          />
        </div>
      )}



      {/* Events Section */}
      {showEvents && selectedWebhook && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Recent Webhook Events</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowEvents(false);
                setSelectedWebhook(null);
                setEvents([]);
              }}
            >
              <EyeOff className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
          <div className="p-6">
            {loadingEvents ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading events...</div>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No events yet for this webhook.</p>
                <p className="text-sm text-gray-400 mt-2">
                  Events will appear here when the webhook is triggered.
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          event.status === 'success' 
                            ? 'bg-green-100 text-green-800'
                            : event.status === 'error'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {event.status}
                        </span>
                        <span className="text-sm font-medium text-gray-700">{event.event_type}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(event.created_at)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-gray-600">Contact:</span>
                        <span className={`ml-2 font-medium ${
                          event.contact_created ? 'text-green-600' : event.contact_updated ? 'text-blue-600' : 'text-gray-400'
                        }`}>
                          {event.contact_created ? 'Created' : event.contact_updated ? 'Updated' : 'No change'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Campaign:</span>
                        <span className={`ml-2 font-medium ${
                          event.campaign_queued ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {event.campaign_queued ? 'Queued' : 'Not queued'}
                        </span>
                      </div>
                      {event.processing_time_ms && (
                        <div>
                          <span className="text-gray-600">Processing:</span>
                          <span className="ml-2 font-medium text-gray-700">{event.processing_time_ms}ms</span>
                        </div>
                      )}
                    </div>
                    {event.error_message && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                        <strong>Error:</strong> {event.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Test Webhook: {selectedWebhook.name}</h2>
              <button
                onClick={() => setShowTestModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Test Data (JSON)
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value && presetTestData[e.target.value]) {
                        setTestData(presetTestData[e.target.value]);
                      }
                    }}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="">Load preset data...</option>
                    <option value="general">General Contact</option>
                    <option value="hubspot">HubSpot Contact</option>
                    <option value="salesforce">Salesforce Opportunity</option>
                    <option value="typeform">Typeform Response</option>
                    <option value="veterinary">Veterinary Appointment</option>
                    <option value="medical">Medical Appointment</option>
                    <option value="dental">Dental Appointment</option>
                  </select>
                </div>
                <textarea
                  value={testData}
                  onChange={(e) => setTestData(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Enter JSON test data..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  This data will be processed by your webhook to test the mapping and campaign triggers.
                </p>
              </div>
              
              {testResult && (
                <div className={`p-4 rounded-lg border ${
                  testResult.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`font-medium ${
                      testResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {testResult.message}
                    </span>
                  </div>
                  
                  {testResult.success && (
                    <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                      <div>
                        <span className="text-gray-600">Contact would be:</span>
                        <span className={`ml-2 font-medium ${
                          testResult.contact_created ? 'text-green-600' : 'text-blue-600'
                        }`}>
                          {testResult.contact_created ? 'Created' : 'Updated'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Campaign:</span>
                        <span className={`ml-2 font-medium ${
                          testResult.campaign_queued ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {testResult.campaign_queued ? 'Would be queued' : 'Not configured'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {testResult.errors && testResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-red-800">Errors:</p>
                      <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                        {testResult.errors.map((error: string, index: number) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {testResult.processing_time_ms && (
                    <div className="mt-2 text-xs text-gray-500">
                      Processing time: {testResult.processing_time_ms}ms
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowTestModal(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => handleTestWebhook(selectedWebhook)}
                  disabled={testing}
                  className="flex items-center gap-2"
                >
                  {testing ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Testing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Test Webhook
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {showCloneModal && webhookToClone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Clone Webhook</h2>
              <button
                onClick={() => setShowCloneModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Webhook: {webhookToClone.name}
                </label>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Name
                </label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter name for cloned webhook..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  The cloned webhook will start in inactive state for safety.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => setShowCloneModal(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleClone}
                  disabled={cloning || !cloneName.trim()}
                  className="flex items-center gap-2"
                >
                  {cloning ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Cloning...
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Clone Webhook
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">🚀 Ready-Made Automations</h2>
                <p className="text-gray-600">Choose a template and we'll set everything up for you</p>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="ml-2">Loading templates...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {webhookTemplates.map((group: any) => {
                  const getCategoryIcon = (category: string) => {
                    if (category === 'crm') return '📊';
                    if (category === 'forms') return '📝';
                    if (category === 'healthcare') return '🏥';
                    return '⚡';
                  };

                  return (
                    <Card key={group.category} className="overflow-hidden">
                      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                        <CardTitle className="flex items-center gap-2">
                          <span className="text-2xl">{getCategoryIcon(group.category)}</span>
                          {group.title}
                        </CardTitle>
                        <p className="text-gray-600">{group.description}</p>
                      </CardHeader>
                      <CardContent className="p-4">
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      {group.templates.map((template: any) => {
                        const getTemplateIcon = (templateId: string) => {
                          if (templateId.includes('hubspot')) return '📊';
                          if (templateId.includes('salesforce')) return '☁️';
                          if (templateId.includes('typeform')) return '📝';
                          if (templateId.includes('google')) return '📋';
                          if (templateId.includes('vet')) return '🐾';
                          if (templateId.includes('dental')) return '🦷';
                          if (templateId.includes('medical')) return '🏥';
                          return '⚡';
                        };

                        const getSuccessBenefit = (templateId: string) => {
                          if (templateId.includes('new_contact') || templateId.includes('welcome')) 
                            return 'Save 3 hours/week on manual welcome emails';
                          if (templateId.includes('surgery') || templateId.includes('post')) 
                            return 'Improve patient care & reduce call volume';
                          if (templateId.includes('appointment')) 
                            return 'Reduce no-shows by 30%';
                          if (templateId.includes('lead')) 
                            return 'Convert 25% more leads to customers';
                          return 'Automate your follow-up process';
                        };

                        return (
                          <Card key={template.id} className="hover:shadow-md transition-shadow border-2 hover:border-blue-200">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-2xl">{getTemplateIcon(template.id)}</span>
                                  <h4 className="font-semibold">{template.name}</h4>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                                    {template.source_platform}
                                  </span>
                                </div>
                              </div>
                              
                              <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                              
                              {/* Success Benefit */}
                              <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3">
                                <div className="flex items-center gap-1 text-green-800 text-xs font-medium">
                                  <CheckCircle className="h-3 w-3" />
                                  {getSuccessBenefit(template.id)}
                                </div>
                              </div>
                              
                              {/* Tags */}
                              <div className="flex flex-wrap gap-1 mb-4">
                                {template.tags.slice(0, 3).map((tag: string) => (
                                  <span key={tag} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    {tag}
                                  </span>
                                ))}
                                {template.tags.length > 3 && (
                                  <span className="text-xs text-gray-500">+{template.tags.length - 3} more</span>
                                )}
                              </div>
                              
                              <Button
                            onClick={() => {
                              // Use template with wizard
                              setSelectedTemplateForWizard(template);
                              setShowTemplates(false);
                              setWizardKey(prev => prev + 1); // Increment key to force remount
                              setShowWizard(true);
                              setSelectedWebhook(null);
                              setShowForm(false);
                              
                              // Scroll to wizard
                              setTimeout(() => {
                                const wizardElement = document.querySelector('[data-wizard="automation-setup"]');
                                if (wizardElement) {
                                  wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }, 100);
                            }}
                                size="sm"
                                className="w-full bg-blue-600 hover:bg-blue-700 font-medium"
                              >
                                <Zap className="h-4 w-4 mr-2" />
                                Set Up in 5 Minutes
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            
            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowTemplates(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedWebhook && tenantId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Edit Automation: {selectedWebhook.name}</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <AutomationWebhookConfig
                webhook={selectedWebhook}
                onSave={async (data) => {
                  try {
                    const { tenant_id, ...updateData } = data as any;
                    await api.automation.updateWebhook(selectedWebhook.id, updateData);
                    toast.success('Automation updated successfully');
                    setShowEditModal(false);
                    if (tenantId) {
                      await loadWebhooks();
                    }
                  } catch (error: any) {
                    console.error('Failed to update automation:', error);
                    toast.error(error?.response?.data?.detail || 'Failed to update automation');
                  }
                }}
                onCancel={() => setShowEditModal(false)}
                tenantId={tenantId}
              />
            </div>
          </div>
        </div>
      )}

      {/* Metrics Modal */}
      {showMetricsModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Automation Statistics: {selectedWebhook.name}</h2>
              <button
                onClick={() => setShowMetricsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            {loadingMetrics ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="ml-2">Loading metrics...</span>
              </div>
            ) : metrics ? (
              <div className="space-y-6">
                {/* Metrics Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{metrics.total_triggers || 0}</div>
                    <div className="text-sm text-gray-600">Total Triggers</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{metrics.successful_triggers || 0}</div>
                    <div className="text-sm text-gray-600">Successful</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{metrics.failed_triggers || 0}</div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {metrics.total_triggers > 0 ? Math.round((metrics.successful_triggers / metrics.total_triggers) * 100) : 0}%
                    </div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Average Processing Time</span>
                    <span className="font-medium">{metrics.avg_processing_time_ms || 0}ms</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Last Triggered</span>
                    <span className="font-medium">{formatDate(selectedWebhook.last_triggered_at)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Status</span>
                    <span className={`font-medium ${selectedWebhook.is_active ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedWebhook.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No metrics available yet. Trigger your automation to see statistics.
              </div>
            )}
            
            <div className="flex justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => setShowMetricsModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
